import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Text, Image,
} from 'grommet';
import { Chat } from 'grommet-icons';
import { PrivacyPolicyModal, TermsOfUseModal } from '@Components/Modal';
import { CurrentDateContext, useAppTheme } from '@Components/Context';
import { SystemStatusNotice } from '@Components/Control';
import { localAsset } from '@Helpers/asset';
import { StyledModalButton, StyledMailtoAnchor } from './StyledFooter';


const Footer = ({
  small,
  companyMaintenanceSettings = null,
  darkModeOverride = undefined,
}) => {
  const [showPrivacyModal, setShowPrivacyModal] = React.useState(false);
  const [showTermsModal, setShowTermsModal] = React.useState(false);

  const { today } = React.useContext(CurrentDateContext);
  const currentYear = today.getUTCFullYear();
  const {
    themeConfig, statusMessageConfig, showStatusMessage, isDarkMode,
  } = useAppTheme();

  const togglePrivacy = () => {
    setShowPrivacyModal(!showPrivacyModal);
  };

  const toggleTerms = () => {
    setShowTermsModal(!showTermsModal);
  };

  const displayStatusNotice = Boolean(
    companyMaintenanceSettings?.showStatusMessage || showStatusMessage,
  );
  const statusMessage = companyMaintenanceSettings?.statusMessage ?? statusMessageConfig?.message;

  // Use override if provided, else context
  const darkMode = typeof darkModeOverride === 'boolean' ? darkModeOverride : isDarkMode;

  const useTheme = darkMode ? themeConfig.darkTheme : themeConfig.lightTheme;
  const { navigation, page } = useTheme;
  const logoSrc = darkMode ? 'images/logo/logo-image-only-white.svg' : 'images/logo/logo-image-only-color.svg';

  const textSize = small ? '0.9rem' : '0.75rem';
  const gapSize = small ? '0.5rem' : '0.3rem';

  return (
    <Box
      fill="horizontal"
      align="center"
      justify="center"
      flex={false}
      pad={{ vertical: small ? '1rem' : '0.75rem' }}
      background={navigation.background}
      gap="1rem"
      direction={small ? 'column-reverse' : 'column'}
    >
      {showPrivacyModal
        && (
        <PrivacyPolicyModal
          toggle={() => togglePrivacy()}
          isSmall={small}
          currentYear={currentYear}
          background={page.modalBg}
          textColor={page.modalText}
        />
        )}
      {showTermsModal
        && (
        <TermsOfUseModal
          toggle={() => toggleTerms()}
          isSmall={small}
          currentYear={currentYear}
          background={page.modalBg}
          textColor={page.modalText}
        />
        )}
      <Box direction="row-responsive" gap={gapSize} justify="center" align="center">
        <Box direction="row" gap={gapSize} justify="center" align="center">
          <Box justify="center" align="center" width={small ? '1rem' : '0.85rem'} height={small ? '1rem' : '0.85rem'}>
            <Image
              fit="contain"
              src={localAsset(logoSrc)}
              alt="Redflag AI logo"
              width={small ? '1rem' : '0.85rem'}
              height={small ? '1rem' : '0.85rem'}
            />
          </Box>
          <Box>
            <Text weight={600} margin="none" size={textSize} color={page.primaryText}>
              ©
              {' '}
              {currentYear}
              {' '}
              Redflag Artificial Intelligence Inc. All rights reserved.
            </Text>
          </Box>
        </Box>
        <Box direction="row" gap={gapSize} justify="center" align="center" pad={{ top: small ? '0.3rem' : '0' }}>
          {!small && (<Text size={textSize} color={page.primaryText}>|</Text>)}
          <StyledModalButton
            plain
            onClick={() => togglePrivacy()}
            color={page.primaryText}
            highlight={page.altHighlight}
          >
            <Box flex justify="center" align="center">
              <Text size={textSize}>Privacy Policy</Text>
            </Box>
          </StyledModalButton>
          <Text size={textSize} color={page.primaryText}>|</Text>
          <StyledModalButton
            plain
            onClick={() => toggleTerms()}
            color={page.primaryText}
            highlight={page.altHighlight}
          >
            <Box flex justify="center" align="center">
              <Text size={textSize}>Terms of Use</Text>
            </Box>
          </StyledModalButton>
          <Text size={textSize} color={page.primaryText}>|</Text>
          <Box direction="row" gap={gapSize} justify="center" align="center">
            <Box>
              <Chat size="0.7rem" color={page.primaryText} />
            </Box>
            <StyledMailtoAnchor
              href="mailto:contact@redflagai.co"
              target="_blank"
              color={page.primaryText}
              highlight={page.altHighlight}
            >
              <Box flex justify="center" align="center">
                <Text size={textSize}>Send Feedback</Text>
              </Box>
            </StyledMailtoAnchor>
          </Box>
        </Box>
      </Box>
      {displayStatusNotice && statusMessageConfig && (
        <SystemStatusNotice
          statusMessageConfig={statusMessageConfig}
          message={statusMessage}
          gapSize={gapSize}
          textSize={textSize}
        />
      )}
    </Box>
  );
};

Footer.propTypes = {
  small: PropTypes.bool.isRequired,
  companyMaintenanceSettings: PropTypes.shape({
    showStatusMessage: PropTypes.bool,
    statusMessage: PropTypes.string,
  }),
  darkModeOverride: PropTypes.bool,
};

export default Footer;
